import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Typography,
} from "@mui/material";
import {
  BlockOutlined,
  WarningAmberRounded,
  InfoOutlined,
} from "@mui/icons-material";
import { useState } from "react";

const REASON_LABELS = {
  FLEET_NOT_IN_POLICY: "Fleet not in policy",
  DRIVER_NOT_FOUND: "Driver not found",
  STATION_NOT_FOUND: "Station not found",
  STATION_RESTRICTED: "Station restricted",
};

export default function SwapAccessDialog({
  open,
  message,
  denialReason,
  onGoBack,
  onProceed,
}) {
  const [confirmed, setConfirmed] = useState(false);

  const handleProceed = () => {
    setConfirmed(false);
    onProceed();
  };

  const handleGoBack = () => {
    setConfirmed(false);
    onGoBack();
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          bgcolor: "#FEF3F2",
          px: 3,
          pt: 2.5,
          pb: 2,
          borderBottom: "1px solid #FECDCA",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "#FEE4E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <BlockOutlined sx={{ fontSize: 22, color: "#B42318" }} />
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 16, fontWeight: 700, color: "#101828" }}
            >
              Swap access denied
            </Typography>
            {denialReason && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  bgcolor: "#FEE4E2",
                  border: "1px solid #FECDCA",
                }}
              >
                <Typography
                  sx={{ fontSize: 11, fontWeight: 600, color: "#B42318" }}
                >
                  {REASON_LABELS[denialReason] || denialReason}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2.5,
            bgcolor: "#FEF3F2",
            border: "1px solid #FECDCA",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <InfoOutlined
              sx={{ fontSize: 18, color: "#B42318", mt: 0.25, flexShrink: 0 }}
            />
            <Typography
              sx={{ fontSize: 13, color: "#912018", lineHeight: 1.6 }}
            >
              {message}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            borderRadius: 2.5,
            bgcolor: "#FFFCF5",
            border: "1px solid #FEF0C7",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <WarningAmberRounded
              sx={{ fontSize: 18, color: "#DC6803", mt: 0.25, flexShrink: 0 }}
            />
            <Typography
              sx={{ fontSize: 12, color: "#93370D", lineHeight: 1.6 }}
            >
              You can override this restriction and proceed with the swap. This
              action will be logged and may require review.
            </Typography>
          </Box>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              sx={{
                color: "#D0D5DD",
                "&.Mui-checked": { color: "#B42318" },
              }}
            />
          }
          label={
            <Typography
              sx={{ fontSize: 13, color: "#344054", fontWeight: 500 }}
            >
              I understand the restriction and want to proceed anyway
            </Typography>
          }
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1,
          gap: 1,
        }}
      >
        <Button
          onClick={handleGoBack}
          variant="outlined"
          sx={{
            flex: 1,
            py: 1.25,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            color: "#344054",
            borderColor: "#D0D5DD",
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleProceed}
          variant="contained"
          disabled={!confirmed}
          sx={{
            flex: 1,
            py: 1.25,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            bgcolor: "#B42318",
            "&:hover": { bgcolor: "#912018" },
            "&.Mui-disabled": {
              bgcolor: "#D0D5DD",
              color: "#98A2B3",
            },
          }}
        >
          Proceed anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}
